/**
 * Configuration Loader
 * Centralized configuration management with environment-specific overrides
 */

import appConfig from '../../config/app.config.json';

export interface AppConfig {
  app: {
    name: string;
    version: string;
    description: string;
  };
  backend: {
    url: string;
    apiVersion: string;
    timeout: number;
    retries: number;
  };
  keycloak: {
    url: string;
    realm: string;
    clientId: string;
    clientSecret: string;
    timeout: number;
    retries: number;
  };
  api: {
    endpoints: {
      registration: string;
      emailActivation: {
        send: string;
        validateCode: string;
        resendActivation: string;
        cleanup: string;
      };
      health: string;
      healthDetailed: string;
      healthAuth: string;
    };
  };
  modules: {
    [category: string]: {
      [module: string]: string | { [key: string]: string };
    };
  };
  security: {
    tokenStorageKey: string;
    refreshTokenKey: string;
    tokenExpiryBuffer: number;
    csrfEnabled: boolean;
    corsEnabled: boolean;
  };
  features: {
    enableDemoMode: boolean;
    enableAnalytics: boolean;
    enableDebugMode: boolean;
  };
  environment: string;
}

// Environment detection
function detectEnvironment(): string {
  const hostname = window.location.hostname;
  const port = window.location.port;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  const envOverride = import.meta.env.VITE_APP_ENVIRONMENT;

  // 'local' and 'kubernetes' both have empty backend URLs — only honour them on localhost.
  // If the env var is set to one of these but we're on a real hostname, fall through to
  // hostname-based detection so the deployed app still hits the correct backend.
  const isProxyOnlyEnv = envOverride === 'local' || envOverride === 'kubernetes';

  if (envOverride && !isProxyOnlyEnv) {
    return envOverride;
  }

  if (isLocalhost) {
    if (port === '30081' || port === '5176' || port === '5175') {
      return 'kubernetes';
    }
    return 'local';
  }

  if (hostname.includes('staging')) {
    return 'staging';
  }

  if (hostname.includes('optibuild.com')) {
    return 'production';
  }

  // Default to development
  return 'development';
}

// Upgrade http:// → https:// when the page is served over HTTPS to prevent mixed content
function enforceHttps(url: string): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://')) {
    return 'https://' + url.slice(7);
  }
  return url;
}

// Load environment-specific configuration
function loadConfig(): AppConfig {
  const environment = detectEnvironment();
  const envConfig = appConfig.environments[environment as keyof typeof appConfig.environments];

  if (!envConfig) {
    throw new Error(`Configuration not found for environment: ${environment}`);
  }

  // Environment variable overrides
  const config: AppConfig = {
    app: appConfig.app,
    backend: {
      url: enforceHttps(import.meta.env.VITE_BACKEND_URL || envConfig.backend.url),
      apiVersion: import.meta.env.VITE_API_VERSION || envConfig.backend.apiVersion,
      timeout: Number(import.meta.env.VITE_BACKEND_TIMEOUT) || envConfig.backend.timeout,
      retries: Number(import.meta.env.VITE_BACKEND_RETRIES) || envConfig.backend.retries,
    },
    keycloak: {
      url: enforceHttps(import.meta.env.VITE_KEYCLOAK_URL || envConfig.keycloak.url),
      realm: import.meta.env.VITE_KEYCLOAK_REALM || envConfig.keycloak.realm,
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || envConfig.keycloak.clientId,
      clientSecret: import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET || (envConfig.keycloak as any).clientSecret,
      timeout: Number(import.meta.env.VITE_KEYCLOAK_TIMEOUT) || envConfig.keycloak.timeout,
      retries: Number(import.meta.env.VITE_KEYCLOAK_RETRIES) || envConfig.keycloak.retries,
    },
    api: appConfig.api,
    modules: appConfig.modules,
    security: {
      ...appConfig.security,
      csrfEnabled: import.meta.env.VITE_CSRF_ENABLED === 'true' || appConfig.security.csrfEnabled,
      corsEnabled: import.meta.env.VITE_CORS_ENABLED === 'true' || appConfig.security.corsEnabled,
    },
    features: {
      enableDemoMode: import.meta.env.VITE_ENABLE_DEMO_MODE === 'true' || appConfig.features.enableDemoMode,
      enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true' || appConfig.features.enableAnalytics,
      enableDebugMode: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true' || import.meta.env.NODE_ENV === 'development',
    },
    environment,
  };

  if (import.meta.env.DEV && config.features.enableDebugMode) {
    console.group('🔧 Optibuild Configuration');
    console.log('Environment:', environment);
    console.log('Backend URL:', config.backend.url);
    console.log('Keycloak URL:', config.keycloak.url);
    console.log('Client ID:', config.keycloak.clientId);
    console.log('Features:', config.features);
    console.groupEnd();
  }

  return config;
}

// Validate configuration
function validateConfig(config: AppConfig): void {
  const requiredFields = [
    { key: 'keycloak.url', value: config.keycloak.url },
    { key: 'keycloak.clientId', value: config.keycloak.clientId },
  ];

  // Backend URL can be empty (indicates using proxy)
  if (config.backend.url && config.backend.url.trim() !== '') {
    requiredFields.push({ key: 'backend.url', value: config.backend.url });
  }

  const missingFields = requiredFields
    .filter(field => !field.value || field.value.trim() === '')
    .map(field => field.key);

  if (missingFields.length > 0) {
    throw new Error(`Missing required configuration: ${missingFields.join(', ')}`);
  }

  // Validate URLs (allow relative URLs starting with / and empty URLs)
  try {
    if (config.backend.url && !config.backend.url.startsWith('/')) {
      new URL(config.backend.url);
    }
    if (!config.keycloak.url.startsWith('/')) {
      new URL(config.keycloak.url);
    }
  } catch (error) {
    throw new Error(`Invalid URL in configuration: ${error}`);
  }
}

// Export singleton configuration
let cachedConfig: AppConfig | null = null;

export function getConfig(): AppConfig {
  if (!cachedConfig) {
    cachedConfig = loadConfig();
    validateConfig(cachedConfig);
  }
  return cachedConfig;
}

// Utility functions
export function getBackendUrl(path: string = ''): string {
  const config = getConfig();
  let baseUrl = config.backend.url;

  // If baseUrl is empty (development with proxy), use relative paths
  if (!baseUrl) {
    baseUrl = '';
  } else {
    baseUrl = enforceHttps(baseUrl.replace(/\/$/, ''));
  }
  
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // For development with proxy, return path as-is since proxy handles routing
  if (!baseUrl) {
    return cleanPath;
  }
  
  if (config.backend.apiVersion && !path.includes('/api/')) {
    return `${baseUrl}/api/${config.backend.apiVersion}${cleanPath}`;
  }
  
  return `${baseUrl}${cleanPath}`;
}

export function getKeycloakUrl(path: string = ''): string {
  const config = getConfig();
  let baseUrl = config.keycloak.url;
  
  // If baseUrl is empty (development with proxy), use relative paths
  if (!baseUrl) {
    baseUrl = '';
  } else {
    baseUrl = baseUrl.replace(/\/$/, '');
  }
  
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // For development with proxy, return path as-is since proxy handles routing
  if (!baseUrl) {
    return cleanPath;
  }
  
  return `${baseUrl}${cleanPath}`;
}

export function getModuleEndpoint(category: string, module: string, subModule?: string): string {
  const config = getConfig();
  const moduleConfig = config.modules[category];
  
  if (!moduleConfig || !moduleConfig[module]) {
    throw new Error(`Module endpoint not found: ${category}.${module}`);
  }
  
  const endpoint = moduleConfig[module];
  
  // Handle nested structure (like resources.equipment)
  if (subModule && typeof endpoint === 'object') {
    if (!endpoint[subModule]) {
      throw new Error(`Sub-module endpoint not found: ${category}.${module}.${subModule}`);
    }
    return getBackendUrl(endpoint[subModule]);
  }
  
  // Handle simple string endpoints
  if (typeof endpoint === 'string') {
    return getBackendUrl(endpoint);
  }
  
  throw new Error(`Invalid endpoint structure: ${category}.${module}`);
}

export function getApiEndpoint(endpointPath: string): string {
  const config = getConfig();
  const pathParts = endpointPath.split('.');
  
  let endpoint: any = config.api.endpoints;
  for (const part of pathParts) {
    if (endpoint[part] === undefined) {
      throw new Error(`API endpoint not found: ${endpointPath}`);
    }
    endpoint = endpoint[part];
  }
  
  if (typeof endpoint !== 'string') {
    throw new Error(`API endpoint is not a string: ${endpointPath}`);
  }
  
  return getBackendUrl(endpoint);
}

// Export configuration for backward compatibility
export const config = getConfig();